import { useEffect, useState, useSyncExternalStore } from 'react';
import type { Editor } from '@tiptap/core';

import type { TCaptionMap } from '@shared/backendFrontend/captionHelpersFe';

import { buildCaptionsMap } from '../tiptap/nodes/caption/plugins/getTrackCaptionsPlugin';
import { elementList, subscribeElements, wholeDoc } from './editor/controller';

/**
 * Captions in the whole document, for the consumers that must see past the part the editor holds:
 * a reference's label, the insert-reference picker, the document-insights panel.
 *
 * Null while no document is loaded in parts, and every consumer then keeps the editor-built map.
 * Recomputed when the element list changes - a commit - and never on a part swap. In an effect rather
 * than a memo because the first call after a commit re-parses the document.
 *
 * Built here rather than in the model on purpose: the model stays free of app-level modules, so it can
 * be imported from the save path without dragging half the module graph into a circular init.
 */
// The editor is a parameter, not read from DocumentContext: this runs inside the component that
// PROVIDES that context, where `useContext` returns null - which left the map permanently null and
// every caption reference outside the live part rendering as an error.
const useWholeDocumentCaptions = (editor: Editor | null): TCaptionMap | null => {
  const elements = useSyncExternalStore(subscribeElements, elementList);
  const [captions, setCaptions] = useState<TCaptionMap | null>(null);

  useEffect(() => {
    const doc = editor ? wholeDoc(editor) : null;
    setCaptions(doc && elements?.length ? buildCaptionsMap(doc) : null);
  }, [elements, editor]);

  return captions;
};

export default useWholeDocumentCaptions;
